import * as Yup from 'yup';
import Inputmask from 'inputmask';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Unstable_Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import { InputAdornment, CircularProgress } from '@mui/material';

import { useAuthContext } from 'src/auth/hooks';
import { createCheck, getBankAccounts } from 'src/services/finance/finance-service-calls';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';

import RHFDatePicker from './RHFDatepicker';

export default function CheckCreateForm({ currentRequest, openCloseModal, refreshData }) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  const EditStateSchema = Yup.object().shape({
    checkNumber: Yup.string().required('Debe ingresar el número de cheque'),
    bankAccount: Yup.string().required('Debe seleccionar una cuenta bancaria'),
    amount: Yup.number().positive('El monto debe ser mayor a 0').required('Debe ingresar un monto'),
    description: Yup.string().required('Debe ingresar una descripción'),
    templateId: Yup.string().nullable(),
    recievedName: Yup.string().required('Debe ingresar el beneficiario del cheque'),
    checkDate: Yup.date().required('Debe seleccionar la fecha del cheque'),
    createUser: Yup.string().nullable(),
    accountCatalog: Yup.string().nullable(),
    procedentType: Yup.number().required('Debe seleccionar el tipo de procedente'),
    otherAffectedAccount: Yup.string().nullable(),
  });

  const defaultValues = useMemo(
    () => ({
      checkNumber: '',
      bankAccount: '',
      amount: 0,
      description: '',
      templateId: '',
      recievedName: '',
      checkDate: new Date(),
      createUser: '',
      accountCatalog: '',
      procedentType: 5,
      otherAffectedAccount: '',
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(EditStateSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const response = await getBankAccounts();
        if (response.status === 200) {
          setAccounts(response.data);
        } else {
          enqueueSnackbar('Error al cargar las cuentas bancarias', { variant: 'error' });
        }
      } catch (error) {
        enqueueSnackbar('Error de conexión al cargar las cuentas bancarias', { variant: 'error' });
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchAccounts();
  }, [enqueueSnackbar]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload = {
        checkNumber: data.checkNumber,
        bankAccountId: data.bankAccount,
        amount: parseFloat(data.amount),
        description: data.description,
        templateId: data.templateId || crypto.randomUUID(),
        recievedName: data.recievedName,
        checkDate: new Date(data.checkDate).toISOString(),
        createUser: user?.userId || null,
        accountCatalog: data.bankAccount,
        procedentType: parseInt(data.procedentType, 10),
        otherAffectedAccount: data.otherAffectedAccount || crypto.randomUUID(),
      };

      const response = await createCheck(payload);

      if (response.status === 200 || response.status === 201) {
        enqueueSnackbar('Cheque creado satisfactoriamente', { variant: 'success' });
        reset();
        refreshData?.();
        openCloseModal?.onFalse();
      } else {
        enqueueSnackbar('Error al crear el cheque', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Error de conexión al crear el cheque', {
        variant: 'error',
      });
    }
  });

  const renderAccountOptions = () => {
    if (loadingAccounts) {
      return (
        <MenuItem disabled>
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 50 }}>
            <CircularProgress size={20} />
          </Stack>
        </MenuItem>
      );
    }
    if (accounts.length === 0) {
      return <MenuItem disabled>No hay cuentas bancarias disponibles</MenuItem>;
    }

    return accounts.map((option) => (
      <MenuItem
        key={option.id || option.accountId || option.bankAccountId}
        value={option.id || option.accountId || option.bankAccountId}
      >
        {option.name || option.account} -
        {option.bank.bankName || ('' && ` - ${option.accountNumber || ''}`)}
      </MenuItem>
    ));
  };

  const renderProcedentTypeOptions = () => [
    <MenuItem key="5" value={5}>
      Proveedor
    </MenuItem>,
    <MenuItem key="6" value={6}>
      Otros
    </MenuItem>,
  ];

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid xs={12} md={8} justifyContent="center" mb={3}>
        <Stack mb={6} mt={1} spacing={3}>
          <Stack display="flex" flexDirection="row" columnGap={2}>
            <RHFTextField name="checkNumber" label="Número de cheque" />
            <RHFSelect name="bankAccount" label="Cuenta bancaria">
              {renderAccountOptions()}
            </RHFSelect>
          </Stack>

          <Stack display="flex" flexDirection="row" columnGap={2}>
            <RHFSelect name="procedentType" label="Tipo de procedente">
              {renderProcedentTypeOptions()}
            </RHFSelect>
            <RHFDatePicker name="checkDate" label="Fecha del cheque" />
          </Stack>

          <RHFTextField
            name="amount"
            label="Monto"
            inputProps={{
              ref: (input) => {
                if (input && !input.dataset.masked) {
                  Inputmask({
                    alias: 'numeric',
                    groupSeparator: ',',
                    autoGroup: true,
                    digits: 2,
                    digitsOptional: false,
                    placeholder: '0',
                    rightAlign: false,
                    autoUnmask: true,
                    removeMaskOnSubmit: true,
                    clearMaskOnLostFocus: false,
                  }).mask(input);
                  input.dataset.masked = 'true';
                  setTimeout(() => {
                    const length = input.value?.length;
                    input.setSelectionRange(length, length);
                  }, 0);
                }
              },
            }}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>$</Box>
                </InputAdornment>
              ),
            }}
          />

          <RHFTextField name="description" label="Descripción" />

          <RHFTextField name="recievedName" label="Beneficiario" placeholder="A favor de..." />

          <Stack display="flex" flexDirection="row" columnGap={2}>
            <RHFTextField name="templateId" label="ID de Plantilla (Opcional)" />
            <RHFTextField name="otherAffectedAccount" label="Otra Cuenta Afectada (Opcional)" />
          </Stack>
        </Stack>

        <Stack justifyContent="flex-end" direction="row" sx={{ mt: 3, mb: 3 }} spacing={2}>
          <Button
            onClick={() => {
              openCloseModal?.onFalse();
              refreshData?.();
            }}
            variant="contained"
            color="error"
            sx={{ width: '30%' }}
          >
            Cancelar
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            color="success"
            fullWidth
          >
            Guardar
          </LoadingButton>
        </Stack>
      </Grid>
    </FormProvider>
  );
}

CheckCreateForm.propTypes = {
  currentRequest: PropTypes.object,
  openCloseModal: PropTypes.object,
  refreshData: PropTypes.func,
};
